'use client';
/**
 * OUVRIR UN COMPTE À QUELQU'UN, DEPUIS SA FICHE — RQ seul.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ LE VOISIN DE `RattacherCompte`, ET SON CONTRAIRE. Les deux boutons se
 * touchent sur la fiche et font l'inverse l'un de l'autre :
 *
 *   · RELIER  — un compte existe déjà et personne ne le revendique ;
 *   · INVITER — il n'en existe aucun : on en CRÉE un, et la personne reçoit un
 *               lien pour choisir son mot de passe.
 *
 * Les confondre donne deux comptes à une personne, dont un que nul ne
 * réclamera jamais. La fiche le dit en toutes lettres entre les deux gestes,
 * et c'est délibéré : la phrase coûte trois lignes, le compte orphelin se
 * découvre des mois plus tard dans un audit d'accès.
 *
 * ── Ce que ce composant NE refait pas ─────────────────────────────────────
 *
 * ⚠️ NI L'ENVOI, NI SON VERDICT. Tout passe par la route du moteur, qui appelle
 * `inviterCompte` — celle dont le LIEN MORT a été corrigé le 12/09/2026 et
 * dont l'ENVOI NON ATTENDU l'a été le 13/09. Rebâtir l'un ou l'autre ici
 * recréerait exactement les jumeaux de ces deux jours, et la seconde copie
 * finirait par oublier de dire que le courriel n'est pas parti.
 *
 * ⚠️ NI LA LISTE DES PROFILS. Elle vient du SERVEUR (`profilPourFonction`),
 * qui la rend avec le profil SUGGÉRÉ d'après la fonction. Une liste tenue ici
 * finirait par proposer un profil que le moteur refuse — et l'écran ne saurait
 * pas dire pourquoi.
 */
import React from 'react';
import { useGed } from '@/lib/store';
import { fetchEcriture } from '@/lib/ecrire';
import { nomDe } from '@/lib/nom';
import { inputStyle, Selecteur, Modale as Fenetre } from '@/components/ui';
import type { RhPersonnel, PropositionCompte } from '@/lib/types';

import { Bouton } from '@/shared/Bouton';
const MAIL_OK = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

export function InviterCompte({ personne, onFait }: {
  personne: RhPersonnel;
  /** Appelé APRÈS le verdict, pour que l'écran relise la fiche. */
  onFait: () => void;
}) {
  const { showToast, profilPourFonction } = useGed();
  const [ouvert, setOuvert] = React.useState(false);
  const [proposition, setProposition] = React.useState<PropositionCompte | null>(null);
  const [profil, setProfil] = React.useState('');
  /* L'adresse de la FICHE fait foi quand elle existe. La ressaisir créerait une
     seconde adresse pour la même personne, et l'on ne saurait plus à laquelle
     écrire. Quand la fiche n'en a pas, on la demande — et on dit qu'elle y sera
     inscrite, parce que c'est ce que le moteur fait. */
  const adresseFiche = String(personne.email || '').trim();
  const [email, setEmail] = React.useState(adresseFiche);
  const [envoi, setEnvoi] = React.useState(false);

  React.useEffect(() => {
    if (!ouvert) return undefined;
    let vivant = true;
    void profilPourFonction(String(personne.fonction || '')).then((p) => {
      if (!vivant) return;
      setProposition(p);
      /* ⚠️ SUGGÉRÉ, JAMAIS IMPOSÉ — et surtout jamais écrit d'office : le champ
         se pré-remplit, le RQ le change ou le vide. Un profil posé sans que
         personne l'ait choisi donne des accès que personne n'a décidés. */
      setProfil((actuel) => actuel || p?.profil || '');
    });
    return () => { vivant = false; };
  }, [ouvert, personne.fonction, profilPourFonction]);

  const inviter = async () => {
    if (!profil || !MAIL_OK(email)) return;
    setEnvoi(true);
    try {
      const r = await fetchEcriture(`/api/ged/ged/rh/personnel/${encodeURIComponent(personne.orgId)}/inviter`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ profil, email: email.trim() }),
      });
      const d = await r.json().catch(() => null);
      if (!r.ok) { showToast(d?.error?.message || 'Invitation refusée.', 'erreur'); return; }
      setOuvert(false);
      /* ⚠️ DEUX FAITS, DEUX MESSAGES. Le compte est créé dans les deux cas —
         c'est irréversible, et le dire d'abord évite qu'on recommence. Ce qui
         change, c'est que la personne a reçu quelque chose, ou rien. */
      if (d?.courrielEnvoye) {
        showToast(`Compte ouvert et invitation envoyée à ${d.email}.`);
      } else {
        showToast(
          `Le compte de ${nomDe(personne)} est CRÉÉ, mais le courriel n’est PAS parti `
          + `(${d?.motifEchec || 'motif inconnu'}) : elle n’a rien reçu et ne peut pas se connecter. `
          + 'Le lien reste valable 48 h et figure dans le journal du serveur ; relancez l’invitation '
          + 'depuis Paramétrage → Utilisateurs une fois le service de courriel rétabli.',
          'erreur',
        );
      }
      onFait();
    } finally { setEnvoi(false); }
  };

  if (!ouvert) {
    return (
      <Bouton taille="sm" onClick={() => setOuvert(true)}>
        Inviter — ouvrir un compte
      </Bouton>
    );
  }

  const aide = (proposition?.profils || []).find((p) => p.cle === profil)?.aide || '';
  const mailPret = MAIL_OK(email);
  return (
    <Fenetre titre="Ouvrir un compte de connexion" largeur={620} onClose={() => setOuvert(false)}
      pied={<>
        <Bouton taille="sm" onClick={() => setOuvert(false)}>Annuler</Bouton>
        <Bouton variante="primaire" disabled={!profil || !mailPret || envoi} onClick={() => void inviter()}>
          {envoi ? 'Envoi…' : 'Créer le compte et envoyer le lien'}
        </Bouton>
      </>}>
      <div style={{ display: 'grid', gap: 12 }}>
        <p style={{ fontSize: 12.5, margin: 0, lineHeight: 1.55 }}>
          Ouvrir un compte à <strong>{nomDe(personne)}</strong> et lui envoyer un lien pour qu’elle
          choisisse son mot de passe.{' '}
          {/* La garantie qui compte, dite AVANT le geste : cet écran n'engendre,
              n'affiche et ne saisit aucun mot de passe. */}
          <span style={{ color: 'var(--text-muted)' }}>
            Aucun mot de passe n’est engendré ni transmis ici : elle choisira le sien sur la page
            sécurisée. Le lien expire dans 48 heures.
          </span>
        </p>

        <label style={{ fontSize: 12.5 }}>Profil de connexion
          <Selecteur value={profil} onChange={setProfil} style={inputStyle}>
            <option value="">— choisir —</option>
            {(proposition?.profils || []).map((p) => (
              <option key={p.cle} value={p.cle}>{p.label}</option>
            ))}
          </Selecteur>
        </label>
        {/* Le MOTIF de la suggestion, et non la seule suggestion : « Encadrement »
            sans raison se lit comme un défaut de formulaire, et l'on ne sait pas
            s'il faut le changer. */}
        {!!proposition?.motif && (
          <p style={{ fontSize: 11.5, color: 'var(--text-muted)', margin: 0, lineHeight: 1.5 }}>
            Suggestion d’après la fonction « {personne.fonction || '—'} » : {proposition.motif}
          </p>
        )}
        {!!aide && (
          <p style={{ fontSize: 12, color: 'var(--text-body)', margin: 0, lineHeight: 1.5, padding: '9px 11px',
            borderRadius: 'var(--radius-sm)', background: 'var(--surface-sunken)', border: '1px solid var(--border-default)' }}>
            {aide}
          </p>
        )}

        <label style={{ fontSize: 12.5 }}>Adresse de courriel
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="prenom.nom@bio24.tn" style={inputStyle} />
        </label>
        {!adresseFiche && (
          <p style={{ fontSize: 11.5, color: 'var(--amber-600)', margin: 0, lineHeight: 1.5 }}>
            Sa fiche ne porte aucune adresse : celle-ci y sera inscrite en même temps que le compte.
          </p>
        )}
        {!!adresseFiche && email.trim().toLowerCase() !== adresseFiche.toLowerCase() && (
          <p style={{ fontSize: 11.5, color: 'var(--amber-600)', margin: 0, lineHeight: 1.5 }}>
            Vous changez l’adresse de sa fiche ({adresseFiche}) : c’est la nouvelle qui y sera inscrite,
            et c’est à elle que le lien partira.
          </p>
        )}
        {!!email.trim() && !mailPret && (
          <p style={{ fontSize: 11.5, color: 'var(--red-600)', margin: 0 }}>
            Adresse invalide : sans elle, l’invitation ne part nulle part.
          </p>
        )}
      </div>
    </Fenetre>
  );
}
